import React, {
  forwardRef,
  memo,
  useRef,
  useImperativeHandle,
  useCallback,
  useState,
  useMemo
} from 'react';
import { TextAreaWithSuggestions } from '../TextAreaWithSuggestions/TextAreaWithSuggestions';
import { MentionTooltip } from '../CommonTooltip/MentionTooltip/MentionTooltip';
import { useMentionInteractions, useTextParsing } from '../../hooks';
import type { HyperlinkMatch } from '../../hooks/mentions/useTextParsing';
import { classNames } from '../../utils/classNames';
import { hasActionBar } from '../../utils/actionBarUtils';
import { useOverlaySync } from './useOverlaySync';
import { OverlayRenderer } from './OverlayRenderer';
import { dedupeMentionsByRange } from './mergeMentions';

import type { DivWithTextAreaSuggestionsProps } from './DivWithTextAreaSuggestions.types';
import type { Mention } from '../../hooks';
import './DivWithTextAreaSuggestions.css';
// removed unused CommentMention import
import { useInitialMentions } from './useInitialMentions';
import { useProcessedMentions } from './useProcessedMentions';
import { DEFAULT_FILE_ACCEPT } from '../../constants/attachments';

const DivWithTextAreaSuggestionsComponent = forwardRef<
  HTMLTextAreaElement,
  DivWithTextAreaSuggestionsProps
>(
  (
    {
      wrapperClassName,
      wrapperStyle,
      className,
      onMentionsChange,
      onHyperlinksChange,
      enableHyperlinks = true,
      initialMentions = [],
      value = '',
      mentionPopupDisplay,
      mentionTooltipStyle,
      mentionTooltipDisplay,
      actionBarConfig,
      containerRef,
      hasAttachments = false,
      fileInputProps,
      ...props
    },
    ref
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const [hyperlinks, setHyperlinks] = useState<HyperlinkMatch[]>([]);
    // Hyperlink and mention parsing
    const { extractHyperlinks, extractMentions } = useTextParsing();

    const { initialParsedMentions } = useInitialMentions({
      value,
      initialMentions,
      extractMentions,
      onMentionsChange
    });
    const { mentions, handleMentionsChange } = useProcessedMentions(
      initialParsedMentions,
      onMentionsChange
    );
    // Forward ref to actual textarea
    useImperativeHandle(ref, () => textareaRef.current!, []);

    // Parse hyperlinks when value changes
    React.useEffect(() => {
      if (!enableHyperlinks) return;
      const detected = extractHyperlinks(value || '');
      setHyperlinks(detected);
      onHyperlinksChange?.(detected);
    }, [enableHyperlinks, extractHyperlinks, onHyperlinksChange, value]);

    // mentions logic moved to useProcessedMentions

    // removed unused handleHyperlinksChange

    // Function to get mention at a specific position
    const getMentionAtPosition = useCallback(
      (position: number): Mention | undefined => {
        // Check both initial and dynamic mentions
        const allMentions = [...initialParsedMentions, ...mentions];
        return allMentions.find(
          mention => position >= mention.start && position < mention.end
        );
      },
      [mentions, initialParsedMentions]
    );

    // Combine all mentions for interactions
    const allMentionsForInteractions = useMemo(
      () => [...initialParsedMentions, ...mentions],
      [initialParsedMentions, mentions]
    );

    // Use mention interactions hook for tooltip functionality
    const {
      tooltipState,
      handleRichMentionHover,
      handleHyperlinkHover,
      hideTooltip
    } = useMentionInteractions({
      inputRef: textareaRef as any, // Type cast since the hook expects input ref
      mentions: allMentionsForInteractions,
      value,
      enableMentions: true,
      getMentionAtPosition
    });

    // Synchronize overlay scroll and height with textarea
    useOverlaySync(
      textareaRef as unknown as React.RefObject<HTMLTextAreaElement>,
      overlayRef as unknown as React.RefObject<HTMLDivElement>
    );

    // Compute display mentions (deduped)
    const displayMentions = useMemo(
      () => dedupeMentionsByRange([...initialParsedMentions, ...mentions]),
      [initialParsedMentions, mentions]
    );

    // Determine class names
    const textareaClassName = classNames(
      'div-with-textarea-suggestions__textarea',
      'div-with-textarea-suggestions__textarea--with-mentions', // Always hide textarea behind overlay
      className
    );

    const overlayClassName = classNames(
      'div-with-textarea-suggestions__overlay',
      'div-with-textarea-suggestions__overlay--visible' // Always show overlay
    );

    const hasActionBarFlag = hasActionBar(actionBarConfig);

    const wrapperClass = classNames(
      'div-with-textarea-suggestions',
      hasActionBarFlag && 'div-with-textarea-suggestions--with-action-bar',
      hasActionBarFlag &&
        hasAttachments &&
        'div-with-textarea-suggestions--has-attachments',
      wrapperClassName
    );

    return (
      <div
        ref={containerRef as React.Ref<HTMLDivElement>}
        className={wrapperClass}
        style={wrapperStyle}
      >
        <div className="div-with-textarea-suggestions__textarea-container">
          {/* Overlay for styled mentions - always visible */}
          <div ref={overlayRef} className={overlayClassName}>
            <OverlayRenderer
              value={value}
              mentions={displayMentions}
              hyperlinks={hyperlinks}
              onMentionHover={handleRichMentionHover}
              onHyperlinkHover={handleHyperlinkHover}
              onHoverLeave={hideTooltip}
            />
          </div>

          {/* Actual textarea with suggestions - always hidden behind overlay; action bar rendered inside when actionBarConfig present */}
          <TextAreaWithSuggestions
            {...props}
            ref={textareaRef}
            value={value}
            className={textareaClassName}
            onMentionsChange={handleMentionsChange}
            enableHyperlinks={false}
            mentionPopupDisplay={mentionPopupDisplay}
            actionBarConfig={actionBarConfig}
          />
        </div>

        {/* Hidden file input inside wrapper so :focus-within keeps action bar expanded when file dialog is open (no extra state) */}
        {fileInputProps && (
          <input
            ref={fileInputProps.ref as React.Ref<HTMLInputElement>}
            type="file"
            className="div-with-textarea-suggestions__file-input"
            multiple={fileInputProps.multiple !== false}
            accept={fileInputProps.accept ?? DEFAULT_FILE_ACCEPT}
            onChange={fileInputProps.onChange}
            tabIndex={-1}
            aria-hidden
          />
        )}

        {/* Mention Tooltip */}
        <MentionTooltip
          show={tooltipState.show}
          position={tooltipState.position}
          user={tooltipState.user}
          hyperlink={tooltipState.hyperlink}
          loading={tooltipState.loading}
          type={tooltipState.type}
          style={mentionTooltipStyle}
          displayMode={mentionTooltipDisplay}
        />
      </div>
    );
  }
);

DivWithTextAreaSuggestionsComponent.displayName = 'DivWithTextAreaSuggestions';

export const DivWithTextAreaSuggestions = memo(
  DivWithTextAreaSuggestionsComponent
);

export default DivWithTextAreaSuggestions;
